import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"
import CheckboxInput from "Components/molecule/inputs/checkbox-input"
import DataTableTemplate from "Components/templates/data-table-template";
import TreeNameCell from "Components/atoms/tree-name-cell";
import SearchConsultingIncomeForm from "Components/organisms/forms/search-consulting-income-form";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { ConsultingIncomeSliceState, setBranches } from "slices/finance/consulting-income/reducer";
import { getAllConsultingIncome, downloadConsultingIncomeCSV, getConsultingIncomeSearchFields } from "slices/finance/consulting-income/thunk";
import Branch from "models/branch";
import { ConsultingIncome, ConsultingIncomeUI, IConsultingIncomeQuery } from "models/consulting-income";
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar";
import { formatCurrency } from "utils/tools";
import calcPercent from "utils/calc-percent";
import { getExpandedRowModel } from "@tanstack/react-table";
import { compareText, stripDepartmentPrefix } from "utils/department-options";

const selectConsultingIncome = createSelector(
    (state: any) => state.ConsultingIncome,
    ({ report, branches }: ConsultingIncomeSliceState) => ({ report, branches })
)

// Reports look back one month
const { fiscalYear, period } = FiscalCalendar.lastMonth()
const currentFiscalPeriod = fiscalYear * 100 + period

const defaultQuery: IConsultingIncomeQuery = {
    fromFiscalPeriod: currentFiscalPeriod,
    toFiscalPeriod: currentFiscalPeriod,
    page: 0,
    limit: Number.MAX_SAFE_INTEGER,
}

// Old RAM Geotechnical company is closed, so it's left out by default but still selectable for history
const DEFAULT_BRANCH_CODES = ['RAM ABBOTS', 'CALGARY', 'VANCOUVER']
const getDefaultBranchIds = (branches: Branch[]) =>
    branches.filter(b => DEFAULT_BRANCH_CODES.includes(b.code.trim())).map(b => b.id)

const sumRows = (rows: ConsultingIncome[]): ConsultingIncome => {
    const footer: ConsultingIncome = {
        department: "Total",
        actual: 0,
        workInHand: 0,
        budget: 0
    };

    for (let i = 0; i < rows.length; i++) {
        footer.actual = rows[i].actual + footer.actual
        footer.budget = rows[i].budget + footer.budget
        footer.workInHand = rows[i].workInHand + footer.workInHand
    }
    return footer;
}

const ConsultingIncomePage = () => {
    document.title = "Consulting Income | IbexIQ";
    const { handleOpenManual } = useManual("finance/consulting-income");

    const dispatch = useDispatch() as any
    const { report, branches } = useSelector(selectConsultingIncome)

    const [query, setQuery] = useState<IConsultingIncomeQuery>(defaultQuery)
    const [isLoading, setIsLoading] = useState(true)
    const [isExporting, setIsExporting] = useState(false)
    const [activeOnly, setActiveOnly] = useState(true)


    const fiscal = new FiscalCalendar(DEFAULT_FISCAL_START)

    const fiscalPeriodOptions = useMemo(() => {
        const options: { value: string; label: string }[] = []
        const today = new Date()
        const { fiscalYear: currentFY, period: currentPeriod } = fiscal.dateToFiscal(today)

        for (let fy = 2019; fy <= currentFY + 1; fy++) {
            const maxPeriod = fy === currentFY + 1 ? currentPeriod : 12
            for (let p = 1; p <= maxPeriod; p++) {
                options.push({
                    value: fy.toString() + String(p).padStart(2, '0'),
                    label: fiscal.fiscalToLabel(fy, p),
                })
            }
        }

        return options
    }, [])

    const handleChangeQuery = (key: string, value: any) => {
        setQuery(prev => ({ ...prev, [key]: value }))
    }

    const handleSearch = async () => {
        setIsLoading(true)
        await dispatch(getAllConsultingIncome(query))
        setIsLoading(false)
    }

    const handleClearSearch = async () => {
        const cleared = { ...defaultQuery, branchIds: getDefaultBranchIds(branches) }
        setQuery(cleared)
        setIsLoading(true)
        await dispatch(getAllConsultingIncome(cleared))
        setIsLoading(false)
    }

    // Load branches first so the first report fetch uses the default branches, not all of them
    useEffect(() => {
        getConsultingIncomeSearchFields().then(async (fields) => {
            const loadedBranches = fields?.branches ?? []
            dispatch(setBranches(loadedBranches))

            const initialQuery = { ...defaultQuery, branchIds: getDefaultBranchIds(loadedBranches) }
            setQuery(initialQuery)
            await dispatch(getAllConsultingIncome(initialQuery))
            setIsLoading(false)
        })
    }, [])

    const branchOptions = useMemo(
        () => branches.map(b => ({ value: b.id, label: b.name })),
        [branches]
    )

    const handleExportData = () => {
        setIsExporting(true)
        try {
            downloadConsultingIncomeCSV(report.departments, query)
        } finally {
            setIsExporting(false)
        }
    }

    const filterActive = (rows: ConsultingIncome[]) => activeOnly
        ? rows.filter(r => r.actual !== 0 || r.budget !== 0 || r.workInHand !== 0)
        : rows

    // One table: division rows with their departments nested (A-Z, without the PP - / MPP - prefix).
    // Departments without a division are listed at the top level.
    const treeRows = useMemo<ConsultingIncome[]>(() => {
        const clean = (r: ConsultingIncome): ConsultingIncome => ({ ...r, department: stripDepartmentPrefix(r.department) })
        const byName = (a: ConsultingIncome, b: ConsultingIncome) => compareText(a.department, b.department)
        const departments = filterActive(report.departments).map(clean)
        const divisionIds = new Set(report.divisions.map(d => d.id))
        const divisions = filterActive(report.divisions).map(d => ({
            ...clean(d),
            subRows: departments.filter(r => r.divisionId === d.id).sort(byName),
        }))
        const orphans = departments.filter(r => !r.divisionId || !divisionIds.has(r.divisionId))
        return [...divisions, ...orphans].sort(byName)
    }, [report, activeOnly])

    // Totals come from the top-level rows (divisions already include their departments)
    const footerRow = useMemo(() => sumRows(treeRows), [treeRows])

    const buildColumns = (label: string, footerRow: ConsultingIncome): ColumnDef<ConsultingIncome>[] => [
        {
            header: " ",
            columns: [

                {
                    id: 'name',
                    header: label,
                    accessorFn: (row) => row.department,
                    size: 260,
                    cell: ({ row }) => <TreeNameCell row={row}>{row.original.department}</TreeNameCell>,
                    meta: {
                        getFooterClassName: () => 'financial-cell-footer'
                    },
                    footer: () => "Total"
                },
                {
                    id: 'actual',
                    accessorFn: (row) => row.actual,
                    header: "Actual",
                    meta: {
                        getHeaderClassName: () => 'text-end',
                        getHeaderTitle: () => 'Acumatica → Project Transactions → Filter by Account Code (Consulting Income, Accrued Revenue)',
                        getCellClassName: (row: ConsultingIncome) => 'text-end',
                        getCellTitle: () => 'Acumatica → Project Transactions → Filter by Account Code (Consulting Income, Accrued Revenue)',
                        getFooterClassName: () => 'financial-cell-footer text-end'
                    },
                    size: 90,
                    cell: ({ row }) => formatCurrency(row.original.actual),
                    footer: () => formatCurrency(footerRow.actual)
                },
                {
                    id: 'budget',
                    accessorFn: (row) => row.budget,
                    header: "Budget",
                    meta: {
                        getHeaderClassName: () => 'text-end',
                        getHeaderTitle: () => 'Acumatica → Budgets → Filter by Sub Account',
                        getCellClassName: (row: ConsultingIncome) => 'text-end',
                        getCellTitle: () => 'Acumatica → Budgets → Filter by Sub Account',
                        getFooterClassName: () => 'financial-cell-footer text-end'
                    },
                    size: 90,
                    cell: ({ row }) => formatCurrency(row.original.budget),
                    footer: () => formatCurrency(footerRow.budget)
                },
            ]
        },
        {
            header: 'Variance',
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
            },
            columns: [
                {
                    id: 'budgetVarianceValue',
                    accessorFn: (row) => row.actual - row.budget,
                    header: "Value",
                    size: 90,
                    meta: {
                        getHeaderClassName: () => 'text-end',
                        getHeaderTitle: () => 'Actual − Budget',
                        getCellTitle: (row: any) => `Actual − Budget: ${ConsultingIncomeUI.format(row.original.actual - row.original.budget)}`,
                        getCellClassName: (row: ConsultingIncome) => `text-end ${ConsultingIncomeUI.colorClass(row.actual - row.budget)}`,
                        getFooterClassName: () => 'financial-cell-footer text-end'
                    },
                    cell: ({ row }: any) => formatCurrency(row.original.actual - row.original.budget),
                    footer: () => formatCurrency(footerRow.actual - footerRow.budget)
                },
                {
                    id: 'budgetVariancePct',
                    accessorFn: (row) => calcPercent(row.actual, row.budget),
                    header: "%",
                    size: 75,
                    meta: {
                        getHeaderClassName: () => 'text-end',
                        getHeaderTitle: () => '(Actual − Budget) ÷ |Budget| × 100',
                        getCellClassName: (row: ConsultingIncome) => `text-end ${ConsultingIncomeUI.colorClass(calcPercent(row.actual, row.budget))}`,
                        getCellTitle: (row: any) => `% = (Actual − Budget) ÷ |Budget| × 100 = ${ConsultingIncomeUI.format(calcPercent(row.original.actual, row.original.budget))}%`,
                        getFooterClassName: () => 'financial-cell-footer text-end'
                    },
                    cell: ({ row }: any) => `(${calcPercent(row.original.actual, row.original.budget).toFixed(2)}%)`,
                    footer: () => `(${calcPercent(footerRow.actual, footerRow.budget).toFixed(2)}%)`
                },
            ],
        },
        {
            header: "WIH Forecast",
            meta: {
                getHeaderClassName: () => 'text-end',
            },
            columns: [
                {
                    id: 'workInHand',
                    accessorFn: (row) => row.workInHand,
                    meta: {
                        getHeaderClassName: () => 'text-end',
                        getCellClassName: (row: ConsultingIncome) => `text-end ${ConsultingIncomeUI.colorClass(row.workInHand - row.budget)}`,
                        getHeaderTitle: () => "IbexIQ → Opportunity Forecast + Project Forecast",
                        getCellTitle: (_row: any) => "IbexIQ → Opportunity Forecast + Project Forecast",
                        getFooterClassName: () => 'financial-cell-footer text-end'
                    },
                    size: 90,
                    cell: ({ row }: any) => formatCurrency(row.original.workInHand),
                    footer: () => formatCurrency(footerRow.workInHand)

                },
            ]
        },
        {
            header: 'Variance Actual VS Forecast',
            meta: {
                getHeaderClassName: () => 'text-end',
            },
            columns: [
                {
                    id: 'wihVarianceValue',
                    accessorFn: (row) => row.actual - row.workInHand,
                    header: "Value",
                    size: 90,
                    meta: {
                        getCellClassName: (row: ConsultingIncome) => `text-end ${ConsultingIncomeUI.colorClass(row.actual - row.workInHand)}`,
                        getHeaderClassName: () => 'text-end',
                        getHeaderTitle: () => 'Actual − WIF Forecast',
                        getCellTitle: (row: any) => `Actual − WIF Forecast: ${ConsultingIncomeUI.format(row.original.actual - row.original.workInHand)}`,
                        getFooterClassName: () => 'financial-cell-footer text-end'
                    },
                    cell: ({ row }: any) => formatCurrency(row.original.actual - row.original.workInHand),
                    footer: () => formatCurrency(footerRow.actual - footerRow.workInHand)
                },
                {
                    id: 'wihVariancePct',
                    accessorFn: (row) => calcPercent(row.actual, row.workInHand),
                    header: "%",
                    size: 75,
                    meta: {
                        getCellClassName: (row: ConsultingIncome) => `text-end ${ConsultingIncomeUI.colorClass(row.actual - row.workInHand)}`,
                        getHeaderClassName: () => 'text-end',
                        getHeaderTitle: () => '(Actual − WIF Forecast) ÷ |WIF Forecast| × 100',
                        getCellTitle: (row: any) => `% = (Actual − WIF Forecast) ÷ |WIF Forecast| × 100 = ${ConsultingIncomeUI.format(calcPercent(row.original.actual, row.original.workInHand))}%`,
                        getFooterClassName: () => 'financial-cell-footer text-end'
                    },
                    cell: ({ row }: any) => `(${calcPercent(row.original.actual, row.original.workInHand).toFixed(2)}%)`,
                    footer: () => `(${calcPercent(footerRow.actual, footerRow.workInHand).toFixed(2)}%)`

                },
            ],
        },
    ]

    const columns = useMemo(() => buildColumns("Division / Department", footerRow), [footerRow])

    const table = useDataTable({
        data: treeRows,
        columns,
        getSubRows: row => row.subRows,
        getExpandedRowModel: getExpandedRowModel(),
        initialState: { expanded: true },
        initialSorting: [{ id: 'name', desc: false }],
        columnResizeMode: 'onEnd',
        enableColumnResizing: true,
    });

    return (
        <DataTableTemplate
            fullExport={handleExportData}
            title={"Finance"}
            breadcrumbItem={"Consulting Income"}
            table={table}
            columns={columns}
            isLoading={isLoading}
            actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
            tableActions={
                <>
                    <CheckboxInput
                        label="Active Only"
                        checked={activeOnly}
                        onChange={() => setActiveOnly(prev => !prev)}
                    />
                </>
            }
            filters={
                <SearchConsultingIncomeForm
                    query={query}
                    fiscalPeriodOptions={fiscalPeriodOptions}
                    branchOptions={branchOptions}
                    isSearching={isLoading}
                    handleChangeQuery={handleChangeQuery}
                    handleSearch={handleSearch}
                    handleClearSearch={handleClearSearch}
                />
            }
        />
    )
}

export default ConsultingIncomePage;
